import { incrementalSelector } from '../../recording/incremental-selectors.ts'
import { observedCssMatches } from '../lib/observed-selectors.ts'
import { countOperationKey, measureOperation } from '../../lib/operation-timing.ts'
import { z } from 'zod'
import type { OfflineDom, OfflineEl } from '../layout-shift/rrweb.ts'
import { offlineSelectorDom, parseTextQuery, resolvePlaywrightSelector, splitPlaywrightSelector, textMatches } from '../../lib/playwright-selector.js'

const TEXT_SELECTOR_VALUE = z.string()

// A text-bearing scope compound with an optional target inside it: `label:has-text("Email") input` is
// the form the catalog generates for a control labeled by a <label>, and the bare `button:has-text("Continue")`
// is the Playwright text pseudo a sightmap review authors from captured DOM evidence.
const HAS_TEXT = /^(.+?):has-text\(("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')\)(?:\s+(.+))?$/

export function querySightmapSelector(dom: OfflineDom, selector: string, parent?: OfflineEl): OfflineEl[] {
  return measureOperation('polish/sightmap/query.querySightmapSelector', () => {
    countOperationKey('sightmap.selector-query', dom, JSON.stringify([parent?.id ?? null, selector]))
    if (selector.startsWith(':scope > ')) {
      return parent ? [...offlineSelectorDom(dom).query(parent, selector)] : []
    }
    if (selector.startsWith('internal:text=')) {
      if (splitPlaywrightSelector(selector).length > 1) return resolvePlaywrightSelector(offlineSelectorDom(dom), selector) ?? []
      // A lone text step: its innermost bearers, kept incrementally across DOM revisions.
      const query = parseTextQuery(selector)
      if (!query) return []
      const matches = (element: OfflineEl) => textMatches(element.text, query)
      const innermost = (element: OfflineEl) => matches(element) && !element.children.some(matches)
      return incrementalSelector(dom, selector, 'text', id => { const node = dom.getById(id); return !!node && innermost(node) }, () => dom.all().filter(innermost).map(node => node.id))
        .flatMap(id => { const node = dom.getById(id); return node ? [node] : [] })
    }
    // The bare has-text engine form: an element containing the text anywhere inside it, whatever the
    // suffix. It grounds an authored selector in captured DOM; it is not the engine's scope filter.
    if (selector.startsWith('internal:has-text=')) {
      const query = parseTextQuery(selector)
      if (!query) return []
      const matches = (element: OfflineEl) => textMatches(element.text, { text: query.text, exact: false })
      return incrementalSelector(dom, selector, 'text', id => { const node = dom.getById(id); return !!node && matches(node) }, () => dom.all().filter(matches).map(node => node.id))
        .flatMap(id => { const node = dom.getById(id); return node ? [node] : [] })
    }
    const hasText = HAS_TEXT.exec(selector)
    if (hasText) {
      const quoted = hasText[2]!
      let wanted: string
      try {
        wanted = quoted.startsWith("'")
          ? quoted.slice(1, -1).replace(/\\(['\\])/g, '$1')
          : TEXT_SELECTOR_VALUE.parse(JSON.parse(quoted))
      } catch { return [] }
      const scopes = observedCssMatches(dom, hasText[1]!).filter(element => textMatches(element.text, { text: wanted, exact: false }))
      const targets = hasText[3] ? scopes.flatMap(scope => scope.querySelectorAll(hasText[3]!)) : scopes
      return [...new Map(targets.map(target => [target.id, target])).values()]
    }
    return observedCssMatches(dom, selector)
  })
}
